import { BACKEND_URL } from "../../config";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Table-admin RTK Query service (thread #1518, 2026-10-01 — the Positions page;
// thread 1a0fe1049bbfb3d5, 2026-10-02 — the Portal Pages page).
// One service, one set of endpoints, a `table` argument selecting which Airtable
// table is being administered. The BFF enforces the President gate server-side on
// every call and refuses a table key it does not know, so this argument can only
// ever name a table the server already serves.
// Reads hit airtable_mirror (no live Airtable, TL Test 2 PASS).
// updatePosition: PATCH one field on one row (MySQL-first + AT write-back).
// createPosition: POST the Add dialog (mint -> mirror insert -> AT create).
//
// TAGS ARE NAMESPACED BY TABLE. A row id is unique within its table, but the two
// pages share one tag type, so `${table}:${id}` keeps an edit on the Portal Pages
// page from invalidating a row on the Positions page (and vice versa). The old
// un-namespaced `id: recid` / `id: "LIST"` shape is what made that possible.
const normTable = (table) => (table ? String(table) : "position");
const tag = (table, id) => `${normTable(table)}:${id}`;

export const positionAdminApi = createApi({
  reducerPath: "positionAdminApi",
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Position"],
  endpoints: (builder) => ({
    // The whole table in one call — 25 positions, 327 portal pages — plus the
    // field catalog, so each page renders config-driven with no pagination.
    listPositions: builder.query({
      query: (table) =>
        `/position-admin/portal/list?table=${encodeURIComponent(normTable(table))}`,
      providesTags: (result, error, table) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: "Position", id: tag(table, id) })),
              { type: "Position", id: tag(table, "LIST") },
            ]
          : [{ type: "Position", id: tag(table, "LIST") }],
    }),
    getPosition: builder.query({
      query: ({ recid, table }) =>
        `/position-admin/portal/get/${recid}?table=${encodeURIComponent(normTable(table))}`,
      providesTags: (result, error, { recid, table }) => [
        { type: "Position", id: tag(table, recid) },
      ],
    }),
    // Linked-record picker (thread #1500 r15). Names for a link field's
    // options, so a President picks a linked record by NAME instead of typing an
    // Airtable record id. Arg: { table, q?, ids? } — `table` here is a
    // LINK_TABLES key (position/tabc/taskType/…), not an admin table key.
    // Server-side the read is bounded (200 rows) and search-driven; it is not
    // cached long because a stale name in an org picker is worse than a fetch.
    getLinkOptions: builder.query({
      query: ({ table, q = "", ids = [] }) => ({
        url: `/position-admin/portal/link-options`,
        params: { table, ...(q ? { q } : {}), ...(ids.length ? { ids: ids.join(",") } : {}) },
      }),
      keepUnusedDataFor: 30,
    }),
    // Cell editor — PATCH one field on one row.
    // Arg: { recid, field, value, table }
    updatePosition: builder.mutation({
      query: ({ recid, field, value, table }) => ({
        url: `/position-admin/portal/update/${recid}?table=${encodeURIComponent(normTable(table))}`,
        method: "PATCH",
        body: { field, value },
      }),
      // Optimistic update — apply immediately, rollback if BFF returns error.
      async onQueryStarted({ recid, field, value, table }, { dispatch, queryFulfilled, getState }) {
        const t = normTable(table);
        const patches = [];
        for (const { endpointName, originalArgs } of Object.values(
          positionAdminApi.util.selectInvalidatedBy(getState(), [{ type: "Position", id: tag(t, recid) }])
        )) {
          if (endpointName === "listPositions") {
            patches.push(
              dispatch(
                positionAdminApi.util.updateQueryData("listPositions", originalArgs ?? t, (draft) => {
                  const row = draft.data?.find((p) => p.id === recid);
                  if (row) row.fields[field] = value;
                })
              )
            );
          }
          if (endpointName === "getPosition") {
            patches.push(
              dispatch(
                positionAdminApi.util.updateQueryData("getPosition", originalArgs, (draft) => {
                  if (draft.data) draft.data.fields[field] = value;
                })
              )
            );
          }
        }
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (result, error, { recid, table }) => [
        { type: "Position", id: tag(table, recid) },
      ],
    }),
    // Add dialog — POST every walked field at once.
    // Arg: { values: { <catalog key>: value }, table }
    createPosition: builder.mutation({
      query: ({ values, table }) => ({
        url: `/position-admin/portal/create?table=${encodeURIComponent(normTable(table))}`,
        method: "POST",
        body: { values },
      }),
      invalidatesTags: (result, error, { table }) => [
        { type: "Position", id: tag(table, "LIST") },
      ],
    }),
  }),
});

export const {
  useListPositionsQuery,
  useGetPositionQuery,
  useLazyGetLinkOptionsQuery,
  useUpdatePositionMutation,
  useCreatePositionMutation,
} = positionAdminApi;
